import { styled, useTheme } from '@mui/material';
import { FC, useEffect, useMemo, useState } from 'react';
import { Typography } from '../Typography/Typography';
import { esgRiskLevelColors } from '../../theme/colors';
import { ChartDataItem, Company, PieChartData, RISK_LEVEL, USER_ROLE } from '../../types';
import { useAtom, useAtomValue } from 'jotai';
import { activeRoleState, filteredCompaniesIdsState } from '../../state/UIState';
import { ComposurePieChart } from './ComposurePieChart';
import { useCustomChartColors } from '../../hooks/useCustomChartColors';

const Wrapper = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const ChartListWrapper = styled('div')`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  grid-gap: 1rem;
`;

const TitleWrapper = styled('div')`
  display: flex;
  flex-direction: column;
`;

interface Props {
  companies: Company[];
  isIssuers?: boolean;
}

export const ComposureCharts: FC<Props> = ({ companies, isIssuers }) => {
  const { colors } = useTheme();

  const [filteredCompaniesIds] = useAtom(filteredCompaniesIdsState);
  const [filteredCompanies, setFilteredCompanies] = useState(companies);
  const activeRole = useAtomValue(activeRoleState);

  const getCustomChartColor = useCustomChartColors();

  useEffect(() => {
    setFilteredCompanies(companies.filter((company) => filteredCompaniesIds.includes(company.id)));
  }, [companies, filteredCompaniesIds]);

  const chartsData: PieChartData[] = useMemo(() => {
    const riskLevelData = filteredCompanies.reduce<Record<string, ChartDataItem>>(
      (acc, company) => {
        const riskLevelKey = company.esgRiskLevel?.toLowerCase() as Lowercase<RISK_LEVEL>;

        if (!riskLevelKey) return acc;

        if (!acc[riskLevelKey]) {
          acc[riskLevelKey] = {
            name: company.esgRiskLevel,
            value: 0,
            id: riskLevelKey,
            itemStyle: {
              color: esgRiskLevelColors[riskLevelKey],
            },
          };
        } else {
          acc[riskLevelKey].value += 1;
        }
        return acc;
      },
      {
        high: {
          name: 'High',
          value: 0,
          id: 'high',
          itemStyle: { color: esgRiskLevelColors.high },
        },
        medium: {
          name: 'Medium',
          value: 0,
          id: 'medium',
          itemStyle: { color: esgRiskLevelColors.medium },
        },
        low: {
          name: 'Low',
          value: 0,
          id: 'low',
          itemStyle: { color: esgRiskLevelColors.low },
        },
      }
    );

    const esgRiskLevelData = [
      riskLevelData['high'],
      riskLevelData['medium'],
      riskLevelData['low'],
    ].filter((item) => item.value > 0);

    const esgRisCompaniesNum = esgRiskLevelData.reduce(function (a, b) {
      return a + b.value;
    }, 0);

    const companiesBySector = filteredCompanies.reduce<Record<string, ChartDataItem>>(
      (acc, company) => {
        company.sectors.forEach((sector) => {
          if (!acc[sector.name]) {
            acc[sector.name] = { name: sector.name, value: 1, id: sector.name };
          } else {
            acc[sector.name].value += 1;
          }
        });
        return acc;
      },
      {}
    );
    const companiesBySectorData = Object.values(companiesBySector)?.map((data, i) => {
      return {
        ...data,
        itemStyle: {
          color: getCustomChartColor(i),
        },
      };
    });

    const companiesByGeography = filteredCompanies.reduce<Record<string, ChartDataItem>>(
      (acc, company) => {
        if (!company.country) return acc;

        if (!acc[company.country]) {
          acc[company.country] = {
            name: company.country,
            value: 1,
            id: company?.country,
          };
        } else {
          acc[company.country].value += 1;
        }
        return acc;
      },
      {}
    );

    const companiesByGeographyData = Object.values(companiesByGeography)?.map((data, i) => {
      return {
        ...data,
        itemStyle: {
          color: getCustomChartColor(i),
        },
      };
    });

    const companiesLabel = isIssuers ? 'Issuers' : 'Companies';

    return [
      {
        title: `${companiesLabel} by ESG Risk Level`,
        data: esgRiskLevelData,
        totalLabel: companiesLabel,
        totalValue: esgRisCompaniesNum,
      },
      {
        title: `${companiesLabel} by SASB Sector`,
        data: companiesBySectorData,
        totalLabel: 'Sectors',
        totalValue: companiesBySectorData.length,
      },
      {
        title: `${companiesLabel} by Geography`,
        data: companiesByGeographyData,
        totalLabel: 'Regions',
        totalValue: companiesByGeographyData.length,
      },
    ];
  }, [filteredCompanies, getCustomChartColor, isIssuers]);

  const showTitle = useMemo(() => activeRole !== USER_ROLE.ANALYST, [activeRole]);

  return (
    <Wrapper>
      {showTitle && (
        <TitleWrapper>
          <Typography variant='h4' color={colors.primary[90]} style={{ marginBottom: '9px' }}>
            Composure
          </Typography>
        </TitleWrapper>
      )}
      <ChartListWrapper>
        {chartsData.map((item) => (
          <ComposurePieChart
            key={item.title}
            data={item.data}
            title={item.title}
            totalLabel={item.totalLabel}
            totalValue={item.totalValue}
          />
        ))}
      </ChartListWrapper>
    </Wrapper>
  );
};
